import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Logger } from '@saviynt/common';

import { getPersonlization, useLocalize } from '../../../shared/hooks';
import { type ColumnPickerState } from '../data-layer/violationsTabTypes';
import { violationsTabActions } from '../data-store/violationsTabSlice';

/**
 * Custom hook to initialize and manage column picker configuration for the Violations tab view.
 *
 * This hook is responsible for fetching column definitions from an API or falling back to a default configuration
 * if the API fails or is unstable. The resulting list of columns is processed to apply localization, determine default selections,
 * and then dispatched to the Redux store to be used in the UI.
 *
 * @returns {{
 *   initializeViewColumns: () => Promise<boolean>
 * }} An object containing a function `initializeViewColumns` that initializes the column picker state and returns a promise that resolves to `true` upon success.
 *
 * @example
 * const { initializeViewColumns } = useColumnPicker();
 * useEffect(() => {
 *   initializeViewColumns();
 * }, []);
 */
const useColumnPicker = () => {
  const l = useLocalize();
  const dispatch = useDispatch();

  const savedColumns = getPersonlization()?.columns ?? null;

  const defaultColumns = [
    'riskName',
    'riskPriority',
    'actualVsPotential',
    'endpoint',
    'firstName',
    'lastName',
    'accountName',
  ];

  const [fetchColumnPicker] =
    violationsTabActions.useLazyGetColumnPickerQuery();

  const initializeViewColumns = useCallback(async () => {
    try {
      const response = (await fetchColumnPicker(
        'violations_list'
      ).unwrap()) as Record<string, ColumnPickerState[]>;

      const columnsList: ColumnPickerState[] = Object.values(response).flat();
      const modifiedColumnsList = columnsList
        .map((column) => ({
          label: l(column.localizationKey),
          value: column.id,
          isChecked:
            savedColumns !== null
              ? savedColumns.includes(column.id)
              : column.isDefault,
          isDefault: defaultColumns.includes(column.id),
          isDisabled: false,
        }))
        .filter((column) => !column.isDefault)
        .sort((b, a) => Number(a.isChecked) - Number(b.isChecked));

      dispatch(violationsTabActions.setViewColumns(modifiedColumnsList));

      return true;
    } catch (error) {
      Logger.log('Error fetching column picker', error);

      return false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- should be only one time for `defaultColumns`
  }, [dispatch, fetchColumnPicker, l]);

  return { initializeViewColumns };
};

// eslint-disable-next-line import/prefer-default-export
export { useColumnPicker };
